function getLuminance(r: number, g: number, b: number): number {
  // Convert to sRGB
  const sr = r / 255;
  const sg = g / 255;
  const sb = b / 255;

  // Convert sRGB to luminance
  const R = sr <= 0.03928 ? sr / 12.92 : Math.pow((sr + 0.055) / 1.055, 2.4);
  const G = sg <= 0.03928 ? sg / 12.92 : Math.pow((sg + 0.055) / 1.055, 2.4);
  const B = sb <= 0.03928 ? sb / 12.92 : Math.pow((sb + 0.055) / 1.055, 2.4);

  // WCAG luminance formula
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function getContrastRatio(bg: number, fg: number): number {
  const lighter = Math.max(bg, fg);
  const darker = Math.min(bg, fg);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Coerce a workspace-supplied color into the `#RRGGBB` form the rest of this
 * module parses with `slice`. Accepts shorthand (`#abc`) and a missing `#`.
 * Returns null for anything else so callers can fall back to the DS default
 * instead of deriving a ramp from NaN.
 */
export function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;

  const hex = value.trim().replace(/^#/, '');

  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toUpperCase();
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return `#${hex}`.toUpperCase();
  }
  return null;
}

export function hexToRgba(hex: string, alpha: number): string | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;

  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);

  return `rgba(${r},${g},${b},${alpha})`;
}

export function getContrastColor(backgroundColor: string): string {
  // Convert hex to RGB
  const r = parseInt(backgroundColor.slice(1, 3), 16);
  const g = parseInt(backgroundColor.slice(3, 5), 16);
  const b = parseInt(backgroundColor.slice(5, 7), 16);

  // Get luminance of background
  const bgLuminance = getLuminance(r, g, b);

  // Get luminance of white and black
  const whiteLuminance = getLuminance(255, 255, 255);
  const blackLuminance = getLuminance(0, 0, 0);

  // Calculate contrast with white and black
  const whiteContrast = getContrastRatio(bgLuminance, whiteLuminance);
  const blackContrast = getContrastRatio(bgLuminance, blackLuminance);

  // Choose the color with better contrast
  return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
}

export function adjustColorLightness(hex: string, amount: number): string {
  // Convert hex to RGB first
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  // Convert RGB to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  // FIXED: Better lightness adjustment using interpolation
  if (amount > 0) {
    // Lighter: interpolate toward white
    l = l + (1 - l) * amount;
  } else {
    // Darker: interpolate toward black
    l = l * (1 + amount);
  }

  // Ensure we stay in valid range
  l = Math.min(Math.max(0, l), 1);

  // Convert back to RGB
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (s === 0) {
    r1 = g1 = b1 = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r1 = hue2rgb(p, q, h + 1 / 3);
    g1 = hue2rgb(p, q, h);
    b1 = hue2rgb(p, q, h - 1 / 3);
  }

  // Convert to hex
  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}
